import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { TELEGRAM_URL } from "../i18n";
import { trackCTA, fetchMemberCount } from "../lib/track";
import { SocialIcons } from "./SocialIcons";

const fadeUp = (i) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay: 0.12 * i, ease: "easeOut" },
});

export const Hero = ({ t }) => {
  const [members, setMembers] = useState("7,900+");

  useEffect(() => {
    fetchMemberCount()
      .then((c) => setMembers(`${(Math.floor(c / 100) * 100).toLocaleString()}+`))
      .catch(() => {});
  }, []);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-32 pb-24 lg:pt-40 lg:pb-32">
      <div className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-[#2847C8]/30 blur-[140px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2">
        <div>
          <motion.div {...fadeUp(0)} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-[#EAB308] animate-pulse-dot" />
            <span className="font-mono-num text-xs uppercase tracking-[0.2em] text-gray-300">{t.hero.eyebrow}</span>
          </motion.div>

          <motion.h1 {...fadeUp(1)} data-testid="hero-headline" className="mt-8 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
            {t.hero.headlineA}
            <br />
            <span className="text-[#EAB308]">{t.hero.headlineB}</span>
          </motion.h1>

          <motion.p {...fadeUp(2)} className="mt-6 max-w-xl text-base leading-relaxed text-gray-400 md:text-lg">
            {t.hero.sub}
          </motion.p>

          <motion.div {...fadeUp(3)} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              data-testid="cta-telegram-hero"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTA("hero_community")}
              className="group flex items-center gap-3 rounded-full bg-[#EAB308] px-8 py-4 text-base font-bold text-[#0B0E11] shadow-[0_0_40px_rgba(234,179,8,0.25)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#FACC15] hover:shadow-[0_0_60px_rgba(234,179,8,0.4)]"
            >
              <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
              {t.hero.ctaPrimary}
            </a>
          </motion.div>

          <motion.div
            {...fadeUp(4)}
            data-testid="hero-coach-strip"
            className="mt-10 max-w-md rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
          >
            <div className="flex items-center gap-4">
              <img
                src="/award-faisal.jpeg"
                alt="Coach Faisal Jofry"
                className="h-14 w-14 shrink-0 rounded-xl border border-[#EAB308]/40 object-cover"
              />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">{t.hero.coach.meet}</p>
                <p className="font-['Outfit'] text-base font-bold text-white">{t.hero.coach.name}</p>
                <p className="mt-0.5 text-xs leading-snug text-[#EAB308]">{t.hero.coach.cred1}</p>
                <p className="text-xs leading-snug text-gray-400">{t.hero.coach.cred2}</p>
              </div>
            </div>
            <p className="mt-4 border-l-2 border-[#EAB308]/50 pl-3 text-sm italic leading-relaxed text-gray-300">
              "{t.founder.philosophy}"
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.founder.tags.map((tag, i) => (
                <span key={i} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-gray-400">
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp(5)} className="mt-6 flex max-w-md items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">{t.hero.followMe}</span>
            <SocialIcons compact iconSize={16} />
          </motion.div>

          <motion.div {...fadeUp(6)} className="mt-14 grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-8">
            {[
              { value: members, label: t.hero.members },
              { value: "6+", label: t.hero.yearsExp },
              { value: t.hero.selective, label: t.hero.selectiveLabel },
            ].map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <p className="font-mono-num text-2xl font-semibold text-[#EAB308] sm:text-3xl">{s.value}</p>
                <p className="mt-1 text-xs text-gray-500 sm:text-sm">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#EAB308]/40 via-transparent to-[#2847C8]/40 blur-2xl" />
          <div className="relative overflow-hidden rounded-2xl border border-[#EAB308]/40 shadow-2xl">
            <img
              src="/award-faisal.jpeg"
              alt="Faisal Jofry — Most Influential Key Opinion Leader of the Year, 2025 Golden Insight Award by WikiFX"
              data-testid="hero-award-image"
              className="w-full"
            />
          </div>
          <div
            data-testid="hero-award-badge"
            className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-[#EAB308]/50 bg-black/80 px-5 py-2.5 backdrop-blur-2xl"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#EAB308] animate-pulse-dot" />
            <span className="font-mono-num text-[11px] font-semibold tracking-[0.15em] text-[#EAB308]">{t.hero.awardBadge}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
